import { useState, useRef } from "react";
import "@/App.css";
import { HashRouter, Routes, Route } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, Navigation as SwiperNavigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Toaster, toast } from "sonner";
import { 
  Menu, X, ChevronDown, MapPin, Calendar, Clock, Star, 
  Heart, Send, ArrowRight, Quote, Mail,
  Instagram, Facebook, Youtube
} from "lucide-react";

// Static Data - Destinations
const DESTINATIONS = [
  {
    id: "1",
    title: "Nage avec les dauphins sauvages",
    subtitle: "Un rêve éveillé à Sataya (Égypte)",
    duration: "7 Jours",
    dates: "30 Mai - 06 JUIN 2026",
    description: "Un rêve éveillé à Sataya (Égypte)",
    price: "2590€",
    image_url: "https://images.pexels.com/photos/4321166/pexels-photo-4321166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
    is_featured: true,
    link: "https://gillesdelieuze.com/voyage-dauphin/"
  },
  {
    id: "2",
    title: "Madagascar - Au cœur de nos racines",
    subtitle: "Plongez au cœur d'une aventure intérieure",
    duration: "8-12 Jours",
    dates: "22 Novembre - 29 Novembre 2025",
    description: "Plongez au cœur d'une aventure intérieure et collective, entre Océan Indien et mémoire ancestrale",
    price: "2975€",
    image_url: "https://images.pexels.com/photos/31234364/pexels-photo-31234364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
    is_featured: false,
    link: "https://gillesdelieuze.com/madagascar/"
  }
];

// Static Data - Testimonials
const TESTIMONIALS = [
  {
    id: "1",
    name: "Myriam",
    content: "Cher Gilles, comment t'exprimer la reconnaissance immense des bienfaits de ce double voyage si particulier. Mon être intérieur jubile et profite de chaque instant. Plus d'ennui, pas de surenchère, et une certaine paix s'installe... méconnue jusque là.",
    image_url: null
  },
  {
    id: "2",
    name: "Éloïse O.",
    content: "Ce voyage est magnifique, magique, magnétique. Outre la rencontre avec les dauphins et le milieu sous-marin, les enseignements de Gilles apportent la profondeur, la sérénité pour intégrer les messages et aller à la rencontre de Soi. Tout est douceur, respect et communion.",
    image_url: "https://gillesdelieuze.com/wp-content/uploads/2023/07/beautiful-brunette-woman-sitting-on-chair-A2KRG6A.jpg"
  },
  {
    id: "3",
    name: "Valérie R.",
    content: "Une rencontre avec des personnes inconnues tout d'abord puis avec les dauphins, présence de Gilles exceptionnelle qui nous accompagne tous ensemble dans ce voyage. La profondeur de la mer rouge au milieu de la baie de Sataya, où les dauphins surfent en communion avec les humains.",
    image_url: "https://gillesdelieuze.com/wp-content/uploads/2023/04/woman-portrait-model-657753.jpg"
  },
  {
    id: "4",
    name: "Angèle",
    content: "Plus qu'un voyage, c'est un chemin initiatique de transformation profonde, une reconnexion à nos racines et notre famille d'âme. Les enseignements de Gilles viennent amplifier l'énergie des dauphins et nous aident à ouvrir les portes fermées, à guérir de nos blessures.",
    image_url: null
  },
  {
    id: "5",
    name: "Laurence P.",
    content: "Avec Gilles, chaque rencontre devient révélation. Tout ce qu'il touche se métamorphose... se sublime. Il nous transforme simplement par ce qu'IL EST, par cette opportunité qu'il ouvre et où chacun peut se rappeler sa propre lumière.",
    image_url: null
  },
  {
    id: "6",
    name: "Sylvie P.",
    content: "Je ne m'attendais pas à ce que ce soit aussi magique ! Les dauphins d'aussi près, aussi souvent, avec une interaction évidente... La présence de Gilles, sa disponibilité et sa douceur, son implication aussi... Et puis toutes ces personnes qui ont d'une manière ou d'une autre touché mon cœur.",
    image_url: null
  }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } }
};

// Fonction pour scroll vers une section
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// Navigation Component
const NavBar = ({ isScrolled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const navItems = [
    { label: "Destinations", id: "destinations" },
    { label: "À propos", id: "a-propos" },
    { label: "Témoignages", id: "temoignages" },
    { label: "Contact", id: "contact" }
  ];
  
  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => scrollToSection('hero')} className="flex items-center gap-3">
            <span className={`font-heading text-2xl tracking-tight ${isScrolled ? 'text-navy' : 'text-white'}`}>
              Gilles Delieuze
            </span>
          </button>
          
          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <button 
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`nav-link ${isScrolled ? 'text-navy' : 'text-white/90 hover:text-white'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
          
          <div className="hidden md:block">
            <button onClick={() => scrollToSection('waitlist')} className="btn-primary text-xs">
              S'inscrire
            </button>
          </div>
          
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className={isScrolled ? 'text-navy' : 'text-white'} size={24} />
            ) : (
              <Menu className={isScrolled ? 'text-navy' : 'text-white'} size={24} />
            )}
          </button>
        </div>
        
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-white rounded-2xl shadow-elevated mb-4 overflow-hidden"
          >
            <div className="p-6 flex flex-col gap-4">
              {navItems.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => { scrollToSection(item.id); setIsMenuOpen(false); }}
                  className="text-navy font-medium py-2 text-left"
                >
                  {item.label}
                </button>
              ))}
              <button onClick={() => { scrollToSection('waitlist'); setIsMenuOpen(false); }} className="btn-primary text-center mt-2">
                S'inscrire
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section id="hero" ref={ref} className="hero-section relative overflow-hidden">
      <motion.div className="absolute inset-0 z-0" style={{ y }}>
        <img 
          src="https://images.pexels.com/photos/15210605/pexels-photo-15210605.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024&w=1536"
          alt="Underwater sunlight"
          className="w-full h-full object-cover scale-110"
        />
        <div className="hero-overlay absolute inset-0" />
      </motion.div>
      
      <motion.div 
        className="hero-content relative z-10 min-h-screen flex flex-col items-center justify-center text-center px-6"
        style={{ opacity }}
      >
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl">
          <motion.p variants={fadeInUp} className="text-gold-light text-sm md:text-base uppercase tracking-[0.3em] mb-6">
            Voyages Sacrés Initiatiques
          </motion.p>
          
          <motion.h1 variants={fadeInUp} className="font-heading text-5xl md:text-6xl lg:text-7xl text-white font-light tracking-tight mb-6">
            Destinations
          </motion.h1>
          
          <motion.p variants={fadeInUp} className="font-heading text-2xl md:text-3xl text-white/90 italic mb-4">
            Choisissez votre futur voyage
          </motion.p>
          
          <motion.p variants={fadeInUp} className="text-white/70 text-lg md:text-xl font-light max-w-2xl mx-auto mb-12">
            Le voyage nous ramène au cœur de nous
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => scrollToSection('destinations')} className="btn-primary">Découvrir les voyages</button>
            <button onClick={() => scrollToSection('a-propos')} className="btn-outline border-white text-white hover:bg-white hover:text-navy">En savoir plus</button>
          </motion.div>
        </motion.div>
        
        <motion.div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="text-white/60" size={32} />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="a-propos" 
      ref={ref}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F0E6 100%)' }}
    >
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1766414803516-bb96b911b7de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdvbGRlbiUyMHNhbmQlMjB0ZXh0dXJlJTIwcmlwcGxlc3xlbnwwfHx8fDE3NzUxMzcwNjN8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div 
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/10 rounded-3xl transform rotate-3" />
              <img 
                src="https://gillesdelieuze.com/wp-content/uploads/2022/12/d-Gilles-21-650x650.jpg"
                alt="Gilles Delieuze"
                className="relative rounded-2xl shadow-elevated w-full object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-soft">
                <p className="text-4xl font-heading text-primary font-bold">25+</p>
                <p className="text-sm text-navy-muted">Années d'expertise</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            className="lg:col-span-7"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-primary font-semibold text-sm uppercase tracking-[0.2em] mb-4">Pourquoi voyager avec moi ?</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-navy mb-8">
              Une expérience au-delà du simple tourisme
            </h2>
            
            <div className="space-y-6 text-navy leading-relaxed">
              <p className="text-lg">
                Partir avec moi, ce n'est pas simplement découvrir un nouvel horizon. 
                Ce n'est pas un simple voyage, c'est une <em className="text-primary font-medium not-italic">traversée</em> – 
                de lieux d'exception, certes, mais surtout une traversée intérieure, 
                une plongée dans les profondeurs de votre être.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-10 bg-white/60 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-primary" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl text-navy font-medium mb-1">Un voyage, pas du tourisme</h4>
                    <p className="text-sm text-navy/70">Explorer des destinations aux énergies uniques</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Heart className="text-primary" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl text-navy font-medium mb-1">Au cœur de vous-même</h4>
                    <p className="text-sm text-navy/70">Renaître à une version plus libre et lumineuse</p>
                  </div>
                </div>
              </div>
              
              <p className="text-lg">
                Depuis plus de 25 ans, j'accompagne les âmes en quête d'authenticité et de transformation. 
                Avec bienveillance, écoute et guidance, je crée un cadre où chacun peut se déposer 
                en toute sécurité, se laisser traverser par ce qui doit être libéré.
              </p>
            </div>
            
            <button onClick={() => scrollToSection('destinations')} className="btn-primary inline-flex items-center gap-2 mt-8">
              Voir les voyages <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Destinations Section
const DestinationsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="destinations" ref={ref} className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <p className="section-subtitle mb-4">Nos prochains voyages</p>
          <h2 className="section-title">Spécialement pour vous</h2>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {DESTINATIONS.map((dest, index) => (
            <motion.div
              key={dest.id}
              className="destination-card group bg-white rounded-3xl overflow-hidden shadow-soft"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <div className="relative h-72 overflow-hidden">
                <img src={dest.image_url} alt={dest.title} className="image-zoom w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                
                {dest.is_featured && (
                  <div className="price-badge flex items-center gap-1">
                    <Star className="text-primary" size={14} fill="currentColor" />
                    <span className="text-xs font-medium text-navy">Nouveau</span>
                  </div>
                )}
                
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-4 text-white/80 text-sm">
                    <span className="flex items-center gap-1"><Clock size={14} /> {dest.duration}</span>
                    <span className="flex items-center gap-1"><Calendar size={14} /> {dest.dates}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="font-heading text-2xl text-navy mb-2">{dest.title}</h3>
                <p className="text-navy-muted text-sm mb-6">{dest.description}</p>
                
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs text-navy-muted uppercase tracking-wider mb-1">À partir de</p>
                    <p className="text-3xl font-heading text-primary font-semibold">{dest.price}</p>
                  </div>
                  <a href={dest.link} target="_blank" rel="noopener noreferrer" className="btn-outline py-3 px-6 text-xs">Détails</a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    { title: "Destinations énergétiques", description: "Des lieux choisis pour leur puissance énergétique : dauphins, terres ancestrales, légendes vivantes.", icon: MapPin },
    { title: "Équilibre subtil", description: "Alternance entre émerveillement extérieur, introspection et temps d'intégration profonde.", icon: Heart },
    { title: "Approche universelle", description: "Accessible à tous, novices ou explorateurs avancés, dans un cadre simple et chaleureux.", icon: Star }
  ];

  return (
    <section ref={ref} className="py-24 md:py-32" style={{ background: 'linear-gradient(180deg, #F5F0E6 0%, #E8DCC4 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="section-subtitle mb-4">Ce qui rend mes voyages uniques</p>
          <h2 className="section-title max-w-3xl mx-auto">Une initiation vers la version la plus lumineuse de vous-même</h2>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.15 }}
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <feature.icon className="text-primary" size={28} />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">{feature.title}</h3>
              <p className="text-navy-muted text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
        
        <motion.p className="text-center mt-12 text-navy-light max-w-2xl mx-auto" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.8, delay: 0.5 }}>
          En choisissant de voyager avec moi, vous ne partez pas juste pour voir le monde. 
          Vous partez pour le <strong>ressentir</strong>, le <strong>vivre</strong>, et le laisser transformer votre vie.
        </motion.p>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const images = [
    { url: "https://images.pexels.com/photos/5008831/pexels-photo-5008831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800", alt: "Dauphins" },
    { url: "https://images.unsplash.com/photo-1747923597043-522389f02506?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzZXJlbmUlMjBwcmlzdGluZSUyMGJlYWNoJTIwY2xlYXIlMjB3YXRlciUyMG1vcm5pbmd8ZW58MHx8fHwxNzc1MTM3MDYzfDA&ixlib=rb-4.1.0&q=85", alt: "Plage cristalline" },
    { url: "https://images.pexels.com/photos/36139778/pexels-photo-36139778.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200", alt: "Madagascar" },
    { url: "https://images.pexels.com/photos/4321166/pexels-photo-4321166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200", alt: "Dauphins Sataya" }
  ];

  return (
    <section ref={ref} className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="section-subtitle mb-4">Galerie</p>
          <h2 className="section-title">Moments de grâce</h2>
        </motion.div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {images.map((image, index) => (
            <motion.div
              key={index}
              className={`gallery-item ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <img src={image.url} alt={image.alt} className="w-full h-full object-cover" style={{ aspectRatio: '1/1' }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="temoignages" ref={ref} className="py-24 md:py-32" style={{ background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F0E6 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="section-subtitle mb-4">Témoignages</p>
          <h2 className="section-title">Ce qu'ils disent de nos voyages</h2>
        </motion.div>
        
        <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.8, delay: 0.3 }}>
          <Swiper
            modules={[Pagination, Autoplay, SwiperNavigation]}
            spaceBetween={32}
            slidesPerView={1}
            pagination={{ clickable: true }}
            autoplay={{ delay: 6000, disableOnInteraction: false }}
            breakpoints={{ 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
            className="pb-16"
          >
            {TESTIMONIALS.map((testimonial) => (
              <SwiperSlide key={testimonial.id}>
                <div className="testimonial-card rounded-2xl p-8 h-full">
                  <Quote className="text-primary/30 mb-4" size={40} />
                  <p className="text-navy-light text-sm leading-relaxed mb-6 line-clamp-6">{testimonial.content}</p>
                  <div className="flex items-center gap-4 mt-auto">
                    {testimonial.image_url ? (
                      <img src={testimonial.image_url} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-primary font-heading text-lg">{testimonial.name.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <p className="font-heading text-lg text-navy">{testimonial.name}</p>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (<Star key={i} className="text-primary" size={12} fill="currentColor" />))}
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </div>
    </section>
  );
};

// Video Section 1 - Mer Rouge
const VideoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-32 bg-navy">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="text-gold-light text-sm uppercase tracking-[0.2em] mb-4">Vidéo souvenir</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-light">Dernier voyage en Mer Rouge</h2>
        </motion.div>
        
        <motion.div
          className="video-container aspect-video rounded-3xl overflow-hidden shadow-elevated"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/Ny3FHPt0tKY"
            title="Voyage en Mer Rouge"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </motion.div>
      </div>
    </section>
  );
};

// Video Section 2 - Souvenirs
const VideoSection2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-32" style={{ background: 'linear-gradient(180deg, #E8DCC4 0%, #F5F0E6 100%)' }}>
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="text-primary text-sm uppercase tracking-[0.2em] mb-4">Souvenirs</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy font-light">Moments magiques à Sataya</h2>
        </motion.div>
        
        <motion.div
          className="video-container aspect-video rounded-3xl overflow-hidden shadow-elevated"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/-cRRcREmHHA"
            title="Moments magiques à Sataya"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </motion.div>
      </div>
    </section>
  );
};

// Waitlist Section - Lien vers FluentForm
const WaitlistSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="waitlist" ref={ref} className="py-24 md:py-32" style={{ background: 'linear-gradient(180deg, #F5F0E6 0%, #E8DCC4 100%)' }}>
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="section-subtitle mb-4">Liste d'attente Voyage Dauphin 2026</p>
          <h2 className="section-title mb-6">Soyez informé(e) en priorité</h2>
          <p className="text-navy-muted mb-8">
            Si tu souhaites être informé(e) en priorité des prochaines dates de voyages 
            pour nager avec les Dauphins sauvages, inscris-toi ici.
          </p>
        </motion.div>
        
        <motion.div 
          className="bg-white rounded-3xl p-8 md:p-12 shadow-soft text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p className="text-navy-light mb-8">
            Cliquez sur le bouton ci-dessous pour accéder au formulaire d'inscription à la liste d'attente.
          </p>
          <a 
            href="https://gillesdelieuze.com/voyages-gd/#elementor-action%3Aaction%3Dpopup%3Aopen%26settings%3DeyJpZCI6IjEyMTU3IiwidG9nZ2xlIjpmYWxzZX0%3D"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 text-base"
          >
            M'inscrire à la liste d'attente <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Créer le lien mailto
    const mailtoLink = `mailto:gilles@centre-terre-ciel.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(`Nom: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`)}`;
    
    // Ouvrir le client email
    window.location.href = mailtoLink;
    
    toast.success("Votre client email va s'ouvrir pour envoyer le message.");
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <section id="contact" ref={ref} className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }}>
            <p className="section-subtitle mb-4">Contact</p>
            <h2 className="section-title mb-8">Prêt(e) à embarquer ?</h2>
            
            <p className="text-navy-light mb-10 leading-relaxed">
              Vous avez des questions sur nos voyages ? N'hésitez pas à nous contacter. 
              Nous serons ravis de vous accompagner dans la préparation de votre prochaine aventure intérieure.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="text-primary" size={20} />
                </div>
                <div>
                  <p className="text-sm text-navy-muted">Email</p>
                  <a href="mailto:gilles@centre-terre-ciel.com" className="text-navy font-medium hover:text-primary transition-colors">
                    gilles@centre-terre-ciel.com
                  </a>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 mt-10">
              <a href="https://www.instagram.com/gillesdelieuze/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <Instagram size={18} />
              </a>
              <a href="https://www.facebook.com/gillesdelieuze" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <Facebook size={18} />
              </a>
              <a href="https://www.youtube.com/@gillesdelieuze" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <Youtube size={18} />
              </a>
            </div>
          </motion.div>
          
          <motion.form 
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-soft"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Nom</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="form-input-gold rounded-xl h-12 px-4"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="form-input-gold rounded-xl h-12 px-4"
                  placeholder="votre@email.com"
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-navy mb-2">Sujet</label>
              <Input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                required
                className="form-input-gold rounded-xl h-12 px-4"
                placeholder="Sujet de votre message"
              />
            </div>
            
            <div className="mb-8">
              <label className="block text-sm font-medium text-navy mb-2">Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                rows={5}
                className="form-input-gold rounded-xl w-full p-4 resize-none"
                placeholder="Votre message..."
              />
            </div>
            
            <Button type="submit" disabled={isSubmitting} className="w-full btn-primary h-14 text-base flex items-center justify-center gap-2">
              {isSubmitting ? "Envoi en cours..." : (<>Envoyer le message <Send size={18} /></>)}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-navy py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <h3 className="font-heading text-2xl text-white mb-4">Gilles Delieuze</h3>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              Voyages sacrés initiatiques pour une transformation profonde. 
              Plus de 25 ans d'expertise en accompagnement spirituel.
            </p>
          </div>
          
          <div>
            <h4 className="text-white font-medium mb-4">Navigation</h4>
            <ul className="space-y-3">
              {[
                { label: "Destinations", id: "destinations" },
                { label: "À propos", id: "a-propos" },
                { label: "Témoignages", id: "temoignages" },
                { label: "Contact", id: "contact" }
              ].map((item) => (
                <li key={item.id}>
                  <button onClick={() => scrollToSection(item.id)} className="footer-link text-left">{item.label}</button>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-medium mb-4">Légal</h4>
            <ul className="space-y-3">
              <li><a href="https://gillesdelieuze.com/mentions-legales/" target="_blank" rel="noopener noreferrer" className="footer-link">Mentions légales</a></li>
              <li><a href="https://gillesdelieuze.com/politique-de-confidentialite/" target="_blank" rel="noopener noreferrer" className="footer-link">Politique de confidentialité</a></li>
              <li><a href="https://gillesdelieuze.com/cgv/" target="_blank" rel="noopener noreferrer" className="footer-link">CGV</a></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">© 2024 Gilles Delieuze. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
};

// Main Home Component
const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  return (
    <div className="min-h-screen">
      <NavBar isScrolled={isScrolled} />
      <HeroSection />
      <AboutSection />
      <DestinationsSection />
      <FeaturesSection />
      <GallerySection />
      <TestimonialsSection />
      <VideoSection />
      <VideoSection2 />
      <WaitlistSection />
      <ContactSection />
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          style: { background: '#1A2B3C', color: '#fff', borderRadius: '12px' }
        }}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <HashRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </HashRouter>
    </div>
  );
}

export default App;
